import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Container, useMediaQuery, useTheme } from "@mui/material";
import type { DeceasedPerson } from "../../../../../../../bookings/types/edit-booking";
import StreamViewer from "../../../shared-ui/StreamViewer";
import type { ICommentType } from "../../../tabs/MemoriesTab";
import DonationsTab from "../../../tabs/DonationTab";
import { DownloadsTab } from "../../../tabs/DownloadsTab";
import AdminTab from "../../../tabs/admin-tab/AdminTab";
import type {
  IStreamDetails,
  IThemeConfig,
} from "../../../types/memorial-types";
import type { CalendarEvent } from "../../../types/AddtoCalendarutils";
import HeroHeader from "./components/HeroHeader";
import Template2TabBar, { type T2Tab } from "./components/Template2TabBar";
import ObituaryTab from "../../../tabs/OrbituaryTab";
import FavouritesTab from "../../../tabs/FavouritesTab";
import TimelineTab from "../../../tabs/TimelineTab";
import ShareSidebar from "./components/ShareSidebar";
import CommentCardView from "../../../shared-ui/CommentCardView";
import DonationSideCard from "./components/DonationSideCard";
import Template2Footer from "./components/Template2Footer";

export interface FuneralTemplate2Props {
  streams: IStreamDetails[];
  activeStreamIndex: number;
  currentStream: IStreamDetails;
  onStreamChange: (index: number) => void;
  memorialTheme: IThemeConfig;
  deceasedData: DeceasedPerson | null;
  isLoadingDeceased: boolean;
  messages: ICommentType[];
  isAdmin: boolean;
  socketRef: React.RefObject<WebSocket | null>;
  handleApprove: (id: string, approve: boolean) => void;
  handleDelete: (id: string) => void;
  onMessageSent: () => void;

  currentPage: number;
  totalCount: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;

  streamProgram: string | null;
  downloadingProgram: boolean;
  handleDownloadProgram: () => void;

  donationRefreshKey: number;
  fetchStreamDetails: () => Promise<void>;
  onDonationSuccess?: (donationId?: number) => void;

  isStaging?: boolean;
  contactPhone?: string;
  contactEmail?: string;
  facebookUrl?: string;
  linkedinUrl?: string;
  youtubeUrl?: string;
  whatsappUrl?: string;
  calendarEvent?: CalendarEvent | null;
}

export default function FuneralTemplate2({
  streams,
  activeStreamIndex,
  currentStream,
  onStreamChange,
  memorialTheme,
  deceasedData,
  isLoadingDeceased,
  messages,
  isAdmin,
  socketRef,
  handleApprove,
  handleDelete,
  onMessageSent,
  hasNextPage,
  onNextPage,
  donationRefreshKey,
  fetchStreamDetails,
  onDonationSuccess,
  isStaging = false,
  contactPhone,
  contactEmail,
  facebookUrl,
  linkedinUrl,
  youtubeUrl,
  whatsappUrl,
  calendarEvent,
}: FuneralTemplate2Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState<T2Tab>("memories");
  const [highlightDonationId, setHighlightDonationId] = useState<number | undefined>(
    undefined,
  );
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));

  // Deep-link into the Donations tab after a successful redirect-based
  // payment (e.g. EFT Corp / ZimSwitch), and clear the params so a page
  // refresh doesn't keep re-highlighting the same donation.
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    const donationIdParam = searchParams.get("donation_id");
    if (tabParam !== "donations") return;

    setTabValue("donations");
    if (donationIdParam) {
      const parsed = Number(donationIdParam);
      if (!Number.isNaN(parsed)) setHighlightDonationId(parsed);
    }

    const next = new URLSearchParams(searchParams);
    next.delete("tab");
    next.delete("donation_id");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDonationSuccess = (donationId?: number) => {
    setTabValue("donations");
    if (donationId) setHighlightDonationId(donationId);
    onDonationSuccess?.(donationId);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "white" }}>
      <HeroHeader stream={currentStream} deceasedData={deceasedData} />

      <Container maxWidth={isMobile ? "sm" : "lg"} sx={{ pt: 3, pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "flex-start",
            gap: 2,
          }}
        >
          {!isMobile && (
            <ShareSidebar streamData={currentStream} calendarEvent={calendarEvent} />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <StreamViewer
              streams={streams}
              activeStream={activeStreamIndex}
              onStreamChange={onStreamChange}
              memorialTheme={memorialTheme}
              currentStream={currentStream}
              deceasedName={deceasedData?.first_name}
              hideTabs={streams.length <= 1}
              onDonationSuccess={handleDonationSuccess}
            />
          </Box>
          {!isMobile && currentStream.chema_fund_enabled && (
            <DonationSideCard
              streamId={currentStream.id}
              streamTitle={currentStream.title}
              deceasedImage={currentStream.booking?.deceased?.[0]?.person_image}
              refreshKey={donationRefreshKey}
              onDonationSuccess={handleDonationSuccess}
            />
          )}
        </Box>
      </Container>

      <Template2TabBar
        value={tabValue}
        onChange={setTabValue}
        isAdmin={isAdmin}
        donationEnabled={currentStream.chema_fund_enabled ?? false}
        accentColor={memorialTheme.primary}
      />

      <Container maxWidth={isMobile ? "sm" : "lg"} sx={{ py: 3 }}>
        {tabValue === "obituary" && (
          <ObituaryTab
            deceasedData={deceasedData}
            streamData={currentStream}
            isLoadingDeceased={isLoadingDeceased}
            isAdmin={isAdmin}
          />
        )}

        {tabValue === "memories" && (
          <CommentCardView
            messages={messages}
            isAdmin={isAdmin}
            socketRef={socketRef}
            isMobile={isMobile}
            handleApprove={handleApprove}
            handleDelete={handleDelete}
            hasNextPage={hasNextPage}
            onNextPage={onNextPage}
            onMessageSent={onMessageSent}
          />
        )}

        {tabValue === "favourites" && (
          <FavouritesTab
            deceasedId={deceasedData?.id}
            deceasedFirstName={deceasedData?.first_name}
            accentColor={memorialTheme.primary}
            isAdmin={isAdmin}
            variant="embedded"
          />
        )}

        {tabValue === "timeline" && (
          <TimelineTab
            deceasedId={deceasedData?.id}
            deceasedFirstName={deceasedData?.first_name}
            accentColor={memorialTheme.primary}
            isAdmin={isAdmin}
            variant="embedded"
          />
        )}

        {tabValue === "admin" &&
          (isStaging ? (
            <AdminTab />
          ) : (
            <DownloadsTab
              streamId={currentStream?.id?.toString() ?? ""}
              vimeoLink={currentStream?.vimeo_stream_link ?? ""}
              vimeoPassword={currentStream?.vimeo_download_password ?? ""}
              currentStream={currentStream}
            />
          ))}

        {tabValue === "donations" && (
          <Box sx={{ mt: 4 }}>
            <DonationsTab
              streamData={currentStream}
              streamId={currentStream?.id?.toString() ?? ""}
              isAdmin={isAdmin}
              isMobile={isMobile}
              fetchStreamDetails={fetchStreamDetails}
              donationRefreshKey={donationRefreshKey}
              highlightDonationId={highlightDonationId}
            />
          </Box>
        )}
      </Container>
      <Template2Footer
        memorialTheme={memorialTheme}
        contactPhone={contactPhone}
        contactEmail={contactEmail}
        facebookUrl={facebookUrl}
        linkedinUrl={linkedinUrl}
        youtubeUrl={youtubeUrl}
        whatsappUrl={whatsappUrl}
      />
    </Box>
  );
}